import * as Sentry from '@sentry/react';
import api from 'api/axiosInstance';
import type { AccountantAuthenticationDataType } from 'types/auth.types';
import { isExpert } from 'utils/constants';
import type {
  AccountantResponse,
  IAccountant,
  AccountantStatusType,
} from 'types/accountant.types';
import { setExpertAmplitudeUser } from 'api/v2/experts/authentication';
import type { AvailableCountries, AvailableLanguages } from 'types/users.types';
import type {
  GetWorkspaceInvitationsResponse,
  IWorkspace,
} from 'types/workspace.types';
import type { PagingV2Response } from 'types/pagination.types';

const rootPath = '/v3/access/accountants';

export type AccountantSignupBody = {
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  country: string;
  language: string;
  workspace_id?: string;
  itaa_number?: string;
};

export type AccountantSignupResponse = {
  __v: number;
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  country: AvailableCountries;
  language: AvailableLanguages;
  created: string;
  modified: string;
  itaa_number?: string;
  workspace_id?: string;
};

export type MagicLinkBody = {
  email: string;
  redirect_path?: string;
  redirect_uri?: string;
  invitation_token?: string;
};

export type MagicLinkResponse = {
  ok: boolean;
  message: string;
};

export type AccountantUpdateBody = {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  country?: string;
  language?: string;
  itaa_number?: string | null; // Only for Belgium users
};

export type AccountantUpdateResponse = {
  __v: number;
  _id: string;
  workspace_id: string;
  status: AccountantStatusType;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  country: AvailableCountries;
  language: AvailableLanguages;
  created: string;
  modified: string;
  itaa_number?: string;
};

// POST /api/v3/access/accountants/signup
export const createAccountant = (
  body: AccountantSignupBody,
): Promise<AccountantSignupResponse> => api.post(`${rootPath}/signup`, body);

// POST /api/v3/access/accountants/magic-link
export const postMagicLoginUrl = (
  body: MagicLinkBody,
): Promise<MagicLinkResponse> =>
  api.post(`${rootPath}/magic-link`, {
    ...body,
    // use when testing locally
    //redirect_uri: body?.redirect_uri ?? 'https://next.experts.accountable.eu/expertlogin',
    redirect_uri: body?.redirect_uri ?? `${window.location.origin}/expertlogin`,
  });

// POST /api/v3/access/accountants/signin
export type AccountantSignin = {
  token: string;
  invitation_token?: string;
};
export const signinAccountant = (
  body: AccountantSignin,
): Promise<AccountantAuthenticationDataType> => {
  return api
    .post<any, AccountantAuthenticationDataType>(`${rootPath}/signin`, body)
    .then((res) => {
      if (isExpert && res?.accountant?.email) {
        setExpertAmplitudeUser(res.accountant.email);
      }
      return res;
    });
};

export const getAccountantData = (): Promise<IAccountant> => {
  return api.get<any, AccountantResponse>(rootPath).then((res) => {
    if (isExpert && res?.accountant) {
      Sentry.setUser({ id: res.accountant._id, email: res.accountant.email });
      setExpertAmplitudeUser(res.accountant.email);
    }
    return res.accountant;
  });
};

// PATCH /api/v3/access/accountants
export const updateAccountantProfile = (
  body: AccountantUpdateBody,
): Promise<AccountantUpdateResponse> => {
  return api.patch<any, AccountantUpdateResponse>(`${rootPath}`, body);
};

export const signout = (client_id: string): Promise<void> => {
  return api.post(`${rootPath}/signout`, { client_id });
};

type AccountantWorkspacesResponse = {
  workspaces: IWorkspace[];
  paging: PagingV2Response;
};

// GET /api/v3/access/workspaces - Get list of workspaces accountant belongs to
export const getAccountantWorkspaces =
  async (): Promise<AccountantWorkspacesResponse> => {
    return api.get('/v3/access/accountants/workspaces');
  };

// Received invites from others accountants
// GET /api/v3/access/accountants/workspace-invites
export const getReceivedWorkspaceInvitations =
  (): Promise<GetWorkspaceInvitationsResponse> =>
    api.get('/v3/access/accountants/workspace-invites');

// DELETE /api/v3/access/accountants
export type DeleteAccountantResponse = {
  ok: boolean;
  message: string;
};

export const deleteAccountant = (): Promise<DeleteAccountantResponse> =>
  api.delete(`${rootPath}`);
